/**
 * NestBuddy scroll reveal.
 *
 * Reveals [data-nb-reveal] elements once as they enter the viewport, then stops
 * observing them. Elements already on screen at load reveal immediately, so the
 * first paint still animates rather than appearing pre-settled.
 */
(() => {
  const SELECTOR = '[data-nb-reveal]';
  const REVEALED = 'nb-revealed';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const reveal = (el) => el.classList.add(REVEALED);

  /** Reveal everything immediately, used as the no-observer / reduced-motion path. */
  const revealAll = (root) => {
    root.querySelectorAll(SELECTOR).forEach(reveal);
  };

  let observer = null;

  const getObserver = () => {
    if (observer) return observer;
    if (!('IntersectionObserver' in window)) return null;

    observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      },
      {
        // Start slightly before the element is fully in view so the motion
        // finishes around the time it reaches a comfortable reading position.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.05,
      }
    );

    return observer;
  };

  const init = (root = document) => {
    if (prefersReducedMotion.matches) {
      revealAll(root);
      return;
    }

    const obs = getObserver();

    if (!obs) {
      revealAll(root);
      return;
    }

    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.nbRevealBound === '1') return;
      el.dataset.nbRevealBound = '1';
      obs.observe(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // Sections re-render on every change in the theme editor, so newly injected
  // markup needs binding. Reveal instantly there — replaying the animation on
  // each keystroke while editing copy is distracting.
  document.addEventListener('shopify:section:load', (event) => {
    revealAll(event.target);
  });

  // If the visitor switches to reduced motion mid-session, stop holding
  // anything back.
  prefersReducedMotion.addEventListener('change', (event) => {
    if (event.matches) revealAll(document);
  });
})();
